/**
 * Trusted Shops (eTrusted) widgets: load their script once the page is in,
 * mount the below-the-fold ones when they come into view.
 *
 * The widgets are <etrusted-widget> elements printed by
 * snippets/dmh-ts-widget.liquid. Their bootstrap (widget.js/v2, which then
 * fetches one bundle per widget type and a JSON feed per widget) is injected
 * at `load`: the page is complete, in the field the LCP has long been
 * painted, and the service stars under the menu no longer sit in an empty
 * band (21/10/2026: the six-second wait inherited from the trustbadge made
 * the page look unfinished). The trustbadge keeps its own six-second rule in
 * sections/dmh-trustbadge.liquid: 41 KB for a floating element nobody waits
 * for.
 *
 * A section can mark its wrapper `data-dmh-ts-lazy` and keep the widget in
 * a <template>: it is mounted when the wrapper comes within 300 px of the
 * viewport, so the review carousel above the footer costs nothing to a
 * visitor who never scrolls, and its bundle never enters a Lighthouse
 * trace. Blocks in the product section cannot do that: Horizon morphs that
 * section on a variant change against server HTML and would drop a
 * client-mounted child.
 *
 * Product widgets (mini stars, review list) start hidden
 * (snippets/dmh-tokens.liquid) and are revealed only when at least one of the
 * product's SKUs has a grade in Trusted Shops' public feeds: the widgets
 * themselves print an empty state ("not reviewed yet") that the merchant
 * does not want (21/10/2026). The check runs at `load`, in parallel with the
 * bootstrap: the product widgets' elements are taken out of the DOM first,
 * so the bootstrap cannot start them, then the channel comes from the first
 * widget's manifest and one small JSON per SKU; a grade puts the elements
 * back (they start on insertion) and sets a class on <html>, outside the
 * product section that Horizon morphs on a variant change, so it survives;
 * the SKU list is product-level anyway. If the feeds cannot be read the
 * widgets are shown, as before the check. On a product without reviews the
 * elements stay out, so their bundles and feeds are never requested (a
 * variant change puts them back, hidden, through the morph: a small cost,
 * only then).
 *
 * Loaded once, as a module, from sections/dmh-trustbadge.liquid at the end
 * of the body: a tag per widget put the first one in the header, ahead of
 * the LCP image in the network queue (A/B on the preview, 21/10/2026).
 */
(function () {
  var doc = window.document;
  var LAZY = '[data-dmh-ts-lazy]';
  if (!doc.querySelector('etrusted-widget') && !doc.querySelector(LAZY)) return;

  var SRC = 'https://integrations.etrusted.com/applications/widget.js/v2';
  var ETRUSTED = 'https://integrations.etrusted.com/';
  var loaded = false;
  var injected = false;

  function inject() {
    if (injected || !doc.querySelector('etrusted-widget')) return;
    injected = true;
    // The Trusted Shops app embed prints the same script when it is on.
    if (doc.querySelector('script[src="' + SRC + '"]')) return;
    var s = doc.createElement('script');
    s.src = SRC;
    s.async = true;
    doc.head.appendChild(s);
  }

  // --- sections marked lazy: the widget waits in a <template> -------------

  // True once mounted; a product wrapper may be empty while its fate is
  // checked (below), then it stays observed.
  function mount(wrapper) {
    var tpl = wrapper.querySelector('template');
    if (!tpl) return false;
    wrapper.appendChild(tpl.content.firstElementChild);
    tpl.remove();
    if (loaded) inject();
    return true;
  }

  function watchLazy() {
    var wrappers = doc.querySelectorAll(LAZY);
    if (!wrappers.length) return;
    if (!window.IntersectionObserver) {
      Array.prototype.forEach.call(wrappers, mount);
      return;
    }
    var io = new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        if (mount(entry.target)) io.unobserve(entry.target);
      });
    }, { rootMargin: '300px 0px' });
    Array.prototype.forEach.call(wrappers, function (w) { io.observe(w); });
  }

  // --- product widgets: hidden until a SKU has reviews ---------------------

  function widgetOf(wrapper) {
    var tpl = wrapper.querySelector('template');
    return wrapper.querySelector('etrusted-widget') || (tpl && tpl.content.firstElementChild) || null;
  }

  function hex(str) {
    return Array.prototype.map.call(new TextEncoder().encode(str), function (b) {
      return ('0' + b.toString(16)).slice(-2);
    }).join('');
  }

  function getJson(url) {
    return fetch(url).then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; });
  }

  // Takes every product widget (the element, or the <template> of a lazy
  // section) out of its wrapper: [{wrapper, node, widget}].
  function detachProductWidgets() {
    var out = [];
    Array.prototype.forEach.call(doc.querySelectorAll('.dmh-ts-widget--product'), function (w) {
      var node = w.querySelector('etrusted-widget') || w.querySelector('template');
      var widget = widgetOf(w);
      if (!node || !widget) return;
      node.remove();
      out.push({ wrapper: w, node: node, widget: widget });
    });
    return out;
  }

  function reveal(detached) {
    doc.documentElement.classList.add('dmh-ts-product-reviews');
    detached.forEach(function (d) { d.wrapper.appendChild(d.node); });
  }

  // Resolves to true when one of the product's SKUs has a grade.
  function hasReviews(detached) {
    if (!detached.length) return Promise.resolve(false);
    var skus = [];
    detached.forEach(function (d) {
      var list = d.widget.getAttribute('data-sku') || '';
      list.split(',').forEach(function (sku) {
        if (sku && skus.indexOf(sku) < 0) skus.push(sku);
      });
    });
    var widgetId = detached[0].widget.getAttribute('data-etrusted-widget-id');
    if (!widgetId) return Promise.resolve(false);
    return fetch(ETRUSTED + 'manifests/v1/' + widgetId + '.json')
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('manifest ' + r.status)); })
      .then(function (manifest) {
        var channel = manifest && manifest.reference && manifest.reference.id;
        if (!channel) throw new Error('no channel in manifest');
        return Promise.all(skus.map(function (sku) {
          return getJson(ETRUSTED + 'feeds/grades/v1/channels/' + channel + '/products/sku/' + hex(sku) + '/feed.json');
        }));
      })
      .then(function (feeds) {
        return feeds.some(function (f) { return f && Object.keys(f).length > 0; });
      })
      .catch(function () { return true; });
  }

  function ready() {
    loaded = true;
    var detached = detachProductWidgets();
    inject();
    hasReviews(detached).then(function (reviewed) {
      if (!reviewed) return;
      reveal(detached);
      inject();
    });
  }

  watchLazy();
  if (doc.readyState === 'complete') ready();
  else window.addEventListener('load', ready, { once: true });
})();
